import type { WorkTask, WorkTaskBillingSummary } from './work-task-types.js';

export interface WorkTaskBillingReportRow {
  id: string;
  taskNumber: string;
  title: string;
  billingDate: string;
  billingHours: number;
  billingMinutes: number;
  status: WorkTask['status'];
}

export interface WorkTaskBillingReport {
  fromDate: string;
  toDate: string;
  rows: WorkTaskBillingReportRow[];
  totalHours: number;
  totalMinutes: number;
  /** Mutually exclusive reasons: each input task contributes to exactly one count. */
  counts: {
    total: number;
    eligible: number;
    bugs: number;
    unclassified: number;
    notApproved: number;
    notImplemented: number;
    missingHours: number;
    missingBillingDate: number;
    outsidePeriod: number;
    invalidData: number;
  };
}

/** Calendar dates, not timestamps: avoids browser/server time-zone differences. */
export function validWorkTaskDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= days[month - 1]!;
}

const validText = (value: unknown, limit: number): value is string =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= limit &&
  !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value);

const implementedStages = new Set(['implemented', 'test-staging', 'ready-for-production', 'production-completed']);
const lexical = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

const validTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= 40 && Number.isFinite(Date.parse(value));

/** Explicit commercial evidence only; neither an estimate nor an old approval is a charge. */
export function approvedWorkTaskMinutes(task: WorkTask): number | null {
  const minutes = task.approvedBillableMinutes;
  const agreement = task.billingAgreement;
  if (
    task.kind !== 'feature' ||
    task.billingDisposition !== 'billable' ||
    !Number.isSafeInteger(minutes) ||
    minutes! < 1 ||
    minutes! > 120000 ||
    !agreement ||
    !validText(agreement.source, 2000) ||
    !validText(agreement.recordedByUserID, 256) ||
    !validTimestamp(agreement.recordedAt) ||
    (agreement.agreedOn !== null && !validWorkTaskDate(agreement.agreedOn))
  )
    return null;
  return minutes!;
}

/** A malformed legacy marker must not forgive outstanding approved minutes. */
export function hasWorkTaskPayment(task: Pick<WorkTask, 'payment'>): boolean {
  return !!task.payment && validTimestamp(task.payment.paidAt) && validText(task.payment.byUserID, 256);
}

/** Complete set, including archived/completed tasks. Display filters must never be passed here. */
export function buildWorkTaskBillingSummary(tasks: readonly WorkTask[]): WorkTaskBillingSummary {
  if (!Array.isArray(tasks)) throw new Error('TASK_INVALID_BILLING_DATA');
  const summary: WorkTaskBillingSummary = {
    approvedMinutes: 0,
    paidMinutes: 0,
    unpaidMinutes: 0,
    billableTasks: 0,
    undecidedTasks: 0,
  };
  const counts = new Map<string, number>();
  for (const task of tasks) if (task && validText(task._id, 256)) counts.set(task._id, (counts.get(task._id) ?? 0) + 1);
  for (const task of tasks) {
    if (!task || !validText(task._id, 256) || counts.get(task._id) !== 1 || task.kind !== 'feature') continue;
    const minutes = approvedWorkTaskMinutes(task);
    if (minutes === null) {
      if (task.billingDisposition !== 'non-billable') summary.undecidedTasks++;
      continue;
    }
    summary.billableTasks++;
    summary.approvedMinutes += minutes;
    if (hasWorkTaskPayment(task)) summary.paidMinutes += minutes;
  }
  if (!Number.isSafeInteger(summary.approvedMinutes)) throw new Error('TASK_INVALID_BILLING_DATA');
  summary.unpaidMinutes = summary.approvedMinutes - summary.paidMinutes;
  return summary;
}

/** One eligibility policy shared by the preview and the server's full database export. */
export function buildWorkTaskBillingReport(
  tasks: readonly WorkTask[],
  fromDate: string,
  toDate: string,
): WorkTaskBillingReport {
  if (!validWorkTaskDate(fromDate) || !validWorkTaskDate(toDate) || fromDate > toDate)
    throw new Error('TASK_INVALID_BILLING_DATE_RANGE');
  if (!Array.isArray(tasks)) throw new Error('TASK_INVALID_BILLING_DATA');
  const report: WorkTaskBillingReport = {
    fromDate,
    toDate,
    rows: [],
    totalHours: 0,
    totalMinutes: 0,
    counts: {
      total: tasks.length,
      eligible: 0,
      bugs: 0,
      unclassified: 0,
      notApproved: 0,
      notImplemented: 0,
      missingHours: 0,
      missingBillingDate: 0,
      outsidePeriod: 0,
      invalidData: 0,
    },
  };
  // Duplicate IDs are rejected entirely: conflicting duplicates must never change totals
  // depending on the order in which a client supplied them.
  const idCounts = new Map<string, number>();
  for (const task of tasks) {
    if (task && typeof task === 'object' && validText(task._id, 256))
      idCounts.set(task._id, (idCounts.get(task._id) ?? 0) + 1);
  }
  let totalMinutes = 0;
  for (const task of tasks) {
    const counts = report.counts;
    if (!task || typeof task !== 'object' || !validText(task._id, 256) || idCounts.get(task._id) !== 1) {
      counts.invalidData++;
      continue;
    }
    if (task.kind === 'bug') {
      counts.bugs++;
      continue;
    }
    if (task.kind !== 'feature') {
      counts.unclassified++;
      continue;
    }
    if (task.billingDisposition !== 'billable' || !task.billingAgreement) {
      counts.notApproved++;
      continue;
    }
    if (!implementedStages.has(task.status)) {
      counts.notImplemented++;
      continue;
    }
    const minutes = approvedWorkTaskMinutes(task);
    if (minutes === null) {
      counts.missingHours++;
      continue;
    }
    if (!validWorkTaskDate(task.billingDate)) {
      counts.missingBillingDate++;
      continue;
    }
    if (task.billingDate < fromDate || task.billingDate > toDate) {
      counts.outsidePeriod++;
      continue;
    }
    if (!validText(task.title, 500) || !validText(task.taskNumber, 100)) {
      counts.invalidData++;
      continue;
    }
    totalMinutes += minutes;
    if (!Number.isSafeInteger(totalMinutes)) throw new Error('TASK_INVALID_BILLING_DATA');
    report.rows.push({
      id: task._id,
      taskNumber: task.taskNumber,
      title: task.title,
      billingDate: task.billingDate,
      billingHours: minutes / 60,
      billingMinutes: minutes,
      status: task.status,
    });
    counts.eligible++;
  }
  report.rows.sort(
    (a, b) => lexical(a.billingDate, b.billingDate) || lexical(a.taskNumber, b.taskNumber) || lexical(a.id, b.id),
  );
  report.totalHours = totalMinutes / 60;
  report.totalMinutes = totalMinutes;
  return report;
}
